import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Table, Space, Button, Input, Select, Tag, Descriptions, Drawer, message } from 'antd';
import { ReloadOutlined, FileExcelOutlined } from '@ant-design/icons';
import { apiService } from '../../../services/apiService';

interface ShopifyOrderItem {
  id?: number;
  orderPk?: number;
  lineItemId?: string;
  sku?: string | null;
  productId?: string | null;
  variantId?: string | null;
  title?: string;
  variantTitle?: string | null;
  quantity?: number;
  price?: number;
  grams?: number | null;
  taxable?: boolean | null;
  fulfillmentStatus?: string | null;
  totalDiscount?: number | null;
}

interface ShopifyOrderRow {
  id: number;
  shopDomain?: string;
  orderId?: string; // Shopify numeric id as string
  adminGraphqlId?: string;
  name?: string; // e.g. #1001
  orderNumber?: number;
  email?: string;
  phone?: string;
  createdAt?: string;
  processedAt?: string;
  cancelledAt?: string | null;
  financialStatus?: string | null;
  fulfillmentStatus?: string | null;
  currency?: string;
  totalPrice?: number | null;
  subtotalPrice?: number | null;
  totalTax?: number | null;
  presentmentCurrency?: string | null;
  customerName?: string | null;
  customerEmail?: string | null;
  customerPhone?: string | null;
  tags?: string | null;
  itemsCount?: number;
  raw?: any;
}

interface APIListResp<T> {
  success: boolean;
  data: T[];
  pagination?: { page: number; limit: number; total: number; totalPages: number };
}

const ShopifyOrder: React.FC = () => {
  const [orders, setOrders] = useState<ShopifyOrderRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    q: '', // name/order number/email
    financialStatus: undefined as string | undefined,
    fulfillmentStatus: undefined as string | undefined,
  });

  const fetchOrders = useCallback(async (page = pagination.current, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const params: any = {
        page,
        limit: pageSize,
      };
      if (filters.q) params.q = filters.q;
      if (filters.financialStatus) params.financialStatus = filters.financialStatus;
      if (filters.fulfillmentStatus) params.fulfillmentStatus = filters.fulfillmentStatus;
      const resp = await apiService.getShopifyOrders(params);
      if ((resp as any)?.success) {
        setOrders(((resp as any).data || []) as ShopifyOrderRow[]);
        const paginationData = (resp as any).pagination;
        if (paginationData) {
          setPagination(prev => ({
            ...prev,
            current: paginationData.page || page,
            pageSize: paginationData.limit || pageSize,
            total: paginationData.total || 0,
          }));
        }
      } else if (Array.isArray(resp)) {
        setOrders(resp as ShopifyOrderRow[]);
      } else {
        setOrders([]);
      }
    } catch (e) {
      message.error('獲取 Shopify 訂單失敗');
    } finally {
      setLoading(false);
    }
  }, [filters.q, filters.financialStatus, filters.fulfillmentStatus, pagination.current, pagination.pageSize]);

  useEffect(() => { fetchOrders(1, pagination.pageSize); }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchOrders(1, pagination.pageSize), 300);
    return () => clearTimeout(t);
  }, [filters.q, filters.financialStatus, filters.fulfillmentStatus]);

  const handleTableChange = (paginationConfig: any) => {
    const { current, pageSize } = paginationConfig;
    setPagination(prev => ({ ...prev, current, pageSize }));
    fetchOrders(current, pageSize);
  };

  const list = useMemo(() => (orders || []).map((o) => ({
    key: o.id,
    name: o.name,
    orderNumber: o.orderNumber,
    createdAt: o.createdAt,
    financialStatus: o.financialStatus,
    fulfillmentStatus: o.fulfillmentStatus,
    totalPrice: o.totalPrice,
    currency: o.currency,
    customerName: o.customerName,
    itemsCount: o.itemsCount,
    raw: o,
  })), [orders]);

  const columns: any[] = [
    { title: '訂單', dataIndex: 'name', width: 120 },
    { title: '日期', dataIndex: 'createdAt', width: 160, render: (v: string) => v ? new Date(v).toLocaleString() : '-' },
    { title: '財務狀態', dataIndex: 'financialStatus', width: 140, render: (s: string) => <Tag>{s || '-'}</Tag> },
    { title: '履約狀態', dataIndex: 'fulfillmentStatus', width: 140, render: (s: string) => <Tag color={!s || s==='unfulfilled'?'orange': s==='fulfilled'?'green':'blue'}>{s || '-'}</Tag> },
    { title: '總額', dataIndex: 'totalPrice', width: 120, render: (n: number, r: any) => (r.currency || '') + ' ' + (Number(n||0).toFixed(2)) },
    { title: '客戶', dataIndex: 'customerName', width: 220 },
    { title: '品項數', dataIndex: 'itemsCount', width: 100 },
  ];

  const items: ShopifyOrderItem[] = useMemo(() => {
    const arr = (selected?.detail?.items || []) as ShopifyOrderItem[] | undefined;
    return Array.isArray(arr) ? arr : [];
  }, [selected]);

  const openDetail = async (record: any) => {
    try {
      const resp: any = await apiService.getShopifyOrderById(String(record.key));
      if (resp?.success) {
        setSelected({ base: record, detail: resp.data });
      } else {
        setSelected({ base: record, detail: null });
      }
    } catch {
      setSelected({ base: record, detail: null });
    }
  };

  const detail = selected?.detail || selected?.base || {};

  const handleExport = () => {
    try {
      if (!orders?.length) {
        message.warning('沒有數據可導出');
        return;
      }
      const headers = ['訂單','日期','財務狀態','履約狀態','總額','幣別','客戶','品項數'];
      const rows = orders.map(o => [
        o.name,
        o.createdAt ? `\u200B${new Date(o.createdAt).toISOString().split('T')[0]}`: '',
        o.financialStatus||'',
        o.fulfillmentStatus||'',
        String(o.totalPrice||0),
        o.currency||'',
        o.customerName||'',
        String(o.itemsCount||0)
      ]);
      const csvContent = [headers, ...rows].map(r => r.map(x => (x==null?'':String(x)).replace(/"/g,'""')).map(x => /[",\n]/.test(x)?`"${x}"`:x).join(',')).join('\n');
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `shopify_orders_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      message.error('導出失敗');
    }
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        extra={
          <Space>
            <Input
              value={filters.q}
              onChange={(e) => setFilters(prev => ({ ...prev, q: e.target.value }))}
              placeholder="搜尋 訂單/編號/Email"
              allowClear
              style={{ width: 280 }}
            />
            <Select
              placeholder="財務狀態"
              value={filters.financialStatus}
              onChange={(v) => setFilters(prev => ({ ...prev, financialStatus: v }))}
              allowClear
              style={{ width: 160 }}
              options={[
                { label: 'paid', value: 'paid' },
                { label: 'pending', value: 'pending' },
                { label: 'refunded', value: 'refunded' },
                { label: 'partially_refunded', value: 'partially_refunded' },
                { label: 'voided', value: 'voided' },
              ]}
            />
            <Select
              placeholder="履約狀態"
              value={filters.fulfillmentStatus}
              onChange={(v) => setFilters(prev => ({ ...prev, fulfillmentStatus: v }))}
              allowClear
              style={{ width: 160 }}
              options={[
                { label: 'unfulfilled', value: 'unfulfilled' },
                { label: 'partial', value: 'partial' },
                { label: 'fulfilled', value: 'fulfilled' },
              ]}
            />
            <Button icon={<ReloadOutlined />} onClick={() => fetchOrders(pagination.current, pagination.pageSize)} loading={loading}>刷新</Button>
            <Button icon={<FileExcelOutlined />} onClick={handleExport} disabled={!orders?.length}>導出</Button>
          </Space>
        }
      >
        <Table
          rowKey="key"
          dataSource={list}
          columns={columns as any}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} 共 ${total} 條`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          onRow={(record) => ({ onClick: () => openDetail(record) })}
        />
      </Card>

      <Drawer
        title={`訂單詳情 - ${detail?.name || '-'}`}
        placement="right"
        width={720}
        open={!!selected}
        onClose={() => setSelected(null)}
      >
        {selected && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card title="基本資訊">
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="訂單">{detail?.name}</Descriptions.Item>
                <Descriptions.Item label="日期">{detail?.createdAt ? new Date(detail.createdAt).toLocaleString() : '-'}</Descriptions.Item>
                <Descriptions.Item label="財務狀態">{detail?.financialStatus || '-'}</Descriptions.Item>
                <Descriptions.Item label="履約狀態">{detail?.fulfillmentStatus || '-'}</Descriptions.Item>
                <Descriptions.Item label="總額">{`${detail?.currency || ''} ${Number(detail?.totalPrice||0).toFixed(2)}`}</Descriptions.Item>
                <Descriptions.Item label="客戶">{detail?.customerName || '-'}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="商品明細">
              <Table
                rowKey={(r) => r.lineItemId || `${r.sku}-${Math.random()}`}
                dataSource={items}
                pagination={false}
                columns={[
                  { title: 'SKU', dataIndex: 'sku', width: 160 },
                  { title: '名稱', dataIndex: 'title' },
                  { title: '款式', dataIndex: 'variantTitle', width: 140 },
                  { title: '數量', dataIndex: 'quantity', width: 100 },
                  { title: '單價', dataIndex: 'price', width: 120, render: (v: number) => Number(v||0).toFixed(2) },
                ]}
              />
            </Card>
          </Space>
        )}
      </Drawer>
    </Space>
  );
};

export default ShopifyOrder;


